// libraries.
import { useFormContext } from 'react-hook-form';

// hooks.
import { useAutocompleteConfidence } from '@domains/documents-review/hooks/useAutocompleteConfidence';
import { useFieldRequirements } from '@domains/documents-review/hooks/useFieldRequirements';

// domain.
import { CountryCode } from '@domains/country';

// components.
import { InputField } from '@shared/InputField/InputField';
import { NumericField } from '@shared/NumericField/NumericField';
import { DropdownField } from '@shared/DropdownField/DropdownField';
import { PriceInputField } from '@shared/PriceInputField/PriceInputField';
import { MultiSelectDropdownField } from '@shared/MultiSelectDropdownField/MultiSelectDropdownField';

// utils.
import { calculateYears } from '@domains/hermes/utils/calculateYears';
import { DROPDOWN_OPTIONS } from '../../constants';

export const FieldsCO = () => {
  const { watch } = useFormContext();
  const { getConfidenceProps } = useAutocompleteConfidence();
  // Obligatoriedad desde el servicio field_requirements (fallback a la config actual).
  const { isRequired } = useFieldRequirements(CountryCode.CO);

  const hasAdmin = watch('hasAdmin');
  const hasGarage = watch('hasGarage');
  const hasTerrace = watch('hasTerrace');
  const constructionDate = watch('constructionDate');
  const yearsOld = constructionDate ? calculateYears(constructionDate) : '';

  return (
    <>
      <div className="d-flex gap-2">
        <DropdownField
          label="Recibo admin."
          name="hasAdmin"
          options={DROPDOWN_OPTIONS.YES_NO}
          required={isRequired('hasAdmin')}
          {...getConfidenceProps('hasAdmin')}
        />
        {hasAdmin === 1 && (
          <PriceInputField
            label="Valor admin."
            name="adminPrice"
            required={isRequired('adminPrice')}
            {...getConfidenceProps('adminPrice')}
          />
        )}
      </div>
      <InputField
        label="Fecha construcción del inmueble"
        type="date"
        name="constructionDate"
        required={isRequired('constructionDate')}
        {...getConfidenceProps('constructionDate')}
      />
      {constructionDate && (
        <InputField
          label="Antigüedad"
          name="yearsOld"
          placeholder={yearsOld?.toString()}
          disabled
          subLabel="años"
        />
      )}
      <DropdownField
        label="Tipo de Inmueble"
        name="propertyType"
        options={DROPDOWN_OPTIONS.PROPERTY_TYPE_CO}
        required={isRequired('propertyType')}
        {...getConfidenceProps('propertyType')}
      />
      <div className="d-flex gap-2">
        <DropdownField
          label="Tipo de área"
          name="areaType"
          options={DROPDOWN_OPTIONS.AREA_TYPE}
          required={isRequired('areaType')}
          {...getConfidenceProps('areaType')}
        />
        <InputField
          label="Área"
          maxLength={3}
          name="area"
          pattern={/^\d{1,3}$/}
          placeholder="Área"
          subLabel="m²"
          required={isRequired('area')}
          {...getConfidenceProps('area')}
        />
      </div>
      <div className="d-flex gap-2">
        <NumericField
          label="Baños"
          name="baths"
          placeholder="Baños"
          required={isRequired('baths')}
          {...getConfidenceProps('baths')}
        />
        <NumericField
          label="Piso"
          name="floor"
          placeholder="Piso"
          required={isRequired('floor')}
          {...getConfidenceProps('floor')}
        />
      </div>
      <div className="d-flex gap-2">
        <DropdownField
          label="Garaje"
          name="hasGarage"
          options={DROPDOWN_OPTIONS.GARAGE}
          required={isRequired('hasGarage')}
          {...getConfidenceProps('hasGarage')}
        />
        {hasGarage === 'Si' && (
          <NumericField
            label="Cantidad de garajes"
            name="garage"
            placeholder="Garage"
            required={isRequired('garage')}
            {...getConfidenceProps('garage')}
          />
        )}
      </div>
      <div className="d-flex gap-2">
        <DropdownField
          label="Balcón/Terraza"
          name="hasTerrace"
          options={DROPDOWN_OPTIONS.YES_NO}
          required={isRequired('hasTerrace')}
          {...getConfidenceProps('hasTerrace')}
        />
        {hasTerrace === 1 && (
          <InputField
            label="Área libre"
            maxLength={3}
            name="freeArea"
            pattern={/^\d{1,3}$/}
            placeholder="Área"
            subLabel="m²"
            required={isRequired('freeArea')}
            {...getConfidenceProps('freeArea')}
          />
        )}
      </div>
      <NumericField
        label="Elevadores"
        name="elevators"
        placeholder="Elevadores"
        required={isRequired('elevators')}
        {...getConfidenceProps('elevators')}
      />
      <MultiSelectDropdownField
        label="Gravámenes"
        name="liens"
        options={DROPDOWN_OPTIONS.LIEN_LIST}
        required={isRequired('liens')}
        {...getConfidenceProps('liens')}
      />
      <DropdownField
        label="Categoría de Gravamen"
        name="liensCategory"
        options={DROPDOWN_OPTIONS.LIEN_CATEGORY}
        required={isRequired('liensCategory')}
        {...getConfidenceProps('liensCategory')}
      />
      <DropdownField
        label="Banco hipoteca"
        name="lenderBank"
        options={DROPDOWN_OPTIONS.BANK_LIST}
        required={isRequired('lenderBank')}
        {...getConfidenceProps('lenderBank')}
      />
    </>
  );
};
